import { ArchiveBoxIcon, DocumentIcon, PhotoIcon, VideoCameraIcon } from '@heroicons/react/24/outline';
import { toast } from './toast';
import axios from "axios";
import { t } from 'i18next';
import moment from 'moment-timezone';

export const formatTimestamp = (
    utcString: string,
    userTimezone: string = moment.tz.guess()
): string => {
    try {
        const localTime = moment.utc(utcString).tz(userTimezone);
        const now = moment().tz(userTimezone);

        const diffInMinutes = now.diff(localTime, 'minutes');
        const diffInHours = now.diff(localTime, 'hours');
        const diffInDays = now.diff(localTime, 'days');
        if (diffInMinutes < 1) return 'Just now';
        if (diffInMinutes < 60) return `${diffInMinutes}m ago`;
        if (diffInHours < 24) return `${diffInHours}h ago`;
        if (diffInDays === 1) return '1 day ago';
        if (diffInDays < 7) return `${diffInDays} days ago`;

        const formatted = localTime.format('MMM D, h:mm A');
        return formatted;
    } catch (error) {
        return 'Unknown time';
    }
};


export const isValidUrl = (url: string): boolean => {
    return typeof url === "string" && (url.startsWith("http://") || url.startsWith("https://"));
  };

export const convertToYouTubeEmbedUrl = (url: string): string => {
    try {
        const parsedUrl = new URL(url);
        const host = parsedUrl.hostname;

        if (host === "youtu.be") {
            return `https://www.youtube.com/embed/${parsedUrl.pathname.slice(1)}`;
        }

        const videoId = parsedUrl.searchParams.get("v");
        return videoId ? `https://www.youtube.com/embed/${videoId}` : "";
    } catch {
        return "";
    }
};

export const formatEnumString = (value: string): string => {
    if (!value) return "";

    return value
        .toLowerCase()
        .split("_")
        .map(word => word.charAt(0).toUpperCase() + word.slice(1))
        .join(" ");
};
  

export const getInitials = (firstName: string, lastName: string) => {
    return `${firstName.charAt(0)}${lastName.charAt(0)}`.toUpperCase();
};


  export const formatSportName = (sport?: string) => {
    if (!sport || typeof sport !== 'string') return '';
    if (sport.startsWith(t('all_sports'))) return sport;
    return sport.replace('_', ' ').toLowerCase().replace(/\b\w/g, (l) => l.toUpperCase());
  };

  export const formatWeightClassName = (weightClass?: string) => {
    if (!weightClass || typeof weightClass !== 'string') return '';
    if (weightClass.startsWith(t('all_weight_classes'))) return weightClass;
    return weightClass.replace('_', ' ').toLowerCase().replace(/\b\w/g, (l) => l.toUpperCase());
  };

  export const formatPurseName = (purse: string) => {
  if (purse.startsWith(t('all_purses'))) return purse;
  return purse.replace(/_/g, ' ').toLowerCase().replace(/\b\w/g, (l) => l.toUpperCase());
};

export const formatRosterStatusName = (rosterStatus: string) => {
  if (rosterStatus.startsWith(t('all_roster_statuses'))) return rosterStatus;
  return rosterStatus.replace(/_/g, ' ').toLowerCase().replace(/\b\w/g, (l) => l.toUpperCase());
};


export const formatItemName = (item: string) => {
    return item.replace('_', ' ').toLowerCase().replace(/\b\w/g, (l) => l.toUpperCase());
};

export const getFileIcon = (fileName: any) => {
    const ext = fileName.split('.').pop().toLowerCase();
    switch (ext) {
        case 'jpg':
        case 'jpeg':
        case 'png':
        case 'gif':
            return <PhotoIcon className="w-4 h-4" />;
        case 'pdf':
        case 'doc':
        case 'docx':
        case 'txt':
            return <DocumentIcon className="w-4 h-4" />;
        case 'mp4':
        case 'mov':
        case 'avi':
            return <VideoCameraIcon className="w-4 h-4" />;
        case 'zip':
        case 'rar':
        case '7z':
            return <ArchiveBoxIcon className="w-4 h-4" />;
        default:
            return <DocumentIcon className="w-4 h-4" />;
    }
};

export const formatFileSize = (size: number) => {
    if (size < 1024) return size + ' B';
    const kb = size / 1024;
    if (kb < 1024) return kb.toFixed(1) + ' KB';
    const mb = kb / 1024;
    if (mb < 1024) return mb.toFixed(1) + ' MB';
    const gb = mb / 1024;
    return gb.toFixed(1) + ' GB';
};

export const getFileType = (file: File) => {
    const extension = file.name.split(".").pop()?.toLowerCase();
    if (["jpg", "jpeg", "png", "gif", "webp"].includes(extension || "")) {
        return "image";
    } else if (["mp4", "avi", "mov", "wmv"].includes(extension || "")) {
        return "video";
    } else {
        return "document";
    }
};

export const validateAndFilterFiles = (files: File[]) => {
    const images = files.filter((f) => getFileType(f) === "image");
    const videos = files.filter((f) => getFileType(f) === "video");
    const documents = files.filter((f) => getFileType(f) === "document");

    let validFiles: File[] = [];
    let errorMessage = "";

    if (images.length > 20) {
        validFiles = [...validFiles, ...images.slice(0, 20)];
        errorMessage += `Only first 20 images selected. `;
    } else {
        validFiles = [...validFiles, ...images];
    }

    const validVideos = videos
        .filter((video) => {
            const sizeInMB = video.size / (1024 * 1024);
            return sizeInMB <= 50;
        })
        .slice(0, 4);

    if (videos.length > 4) {
        errorMessage += `Only first 4 videos selected. `;
    }
    if (videos.some((v) => v.size / (1024 * 1024) > 50)) {
        errorMessage += `Videos over 50MB excluded. `;
    }

    validFiles = [...validFiles, ...validVideos];

    if (documents.length > 10) {
        validFiles = [...validFiles, ...documents.slice(0, 10)];
        errorMessage += `Only first 10 documents selected. `;
    } else {
        validFiles = [...validFiles, ...documents];
    }

    if (errorMessage) {
        toast.warning(errorMessage.trim());
    }

    return validFiles;
};

export interface ApiErrorResponse {
  message: string;
  statusCode: number;
  timeStamp: string;
}

export const getErrorMessage = (error: unknown, fallback: string): string => {
  if (axios.isAxiosError<ApiErrorResponse>(error)) {
    return error.response?.data?.message ?? fallback;
  }
  return fallback;
};